import axios from "./_axios";
import { API } from "./apiEndPoints";
import { refreshAccountCounts } from "@/utils/accountCountUtils";
import { getListOfPositionsV2 } from "./positionAPI";

function serializeDepartmentListParams(params) {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((v) => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      });
    } else if (value !== undefined && value !== "") {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
}

// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_department_list
export const getListOfDepartments = (account_id, search, filters = {}, pagination = {}) => {
  const params = { ordering: filters.ordering || "name" };
  const trimmedSearch = typeof search === "string" ? search.trim() : "";
  if (trimmedSearch) {
    params.search = trimmedSearch;
  }
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }
  if (filters.archived !== undefined) {
    params.archived = filters.archived;
  }
  if (filters.has_manager !== undefined) {
    params.has_manager = filters.has_manager;
  }
  if (pagination.limit != null) {
    params.limit = pagination.limit;
  }
  if (pagination.offset != null) {
    params.offset = pagination.offset;
  }
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/`,
    params,
    paramsSerializer: serializeDepartmentListParams,
  });
};

// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_department_read
export const getDepartment = (account_id, department_id) => {
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/`,
  });
};

export const getPositionsForDepartment = (
  account_id,
  department_id,
  { search = "", status = [], archived, limit = 10, offset = 0, departmentScope } = {}
) => {
  const params = {
    department_id,
    limit,
    offset,
    ordering: "name",
  };
  const trimmedSearch = typeof search === "string" ? search.trim() : "";
  if (trimmedSearch) {
    params.name = trimmedSearch;
  }
  if (Array.isArray(status) && status.length > 0) {
    params.status = status;
  }
  if (archived !== undefined) {
    params.archived = archived;
  }
  if (departmentScope) {
    params.department_scope = departmentScope;
  }
  return getListOfPositionsV2({
    account_id,
    queryParamsObj: params,
  });
};

export const getOrphanPositions = (
  account_id,
  { search = "", status = [], limit = 10, offset = 0 } = {}
) => {
  const params = {
    orphan: true,
    limit,
    offset,
    ordering: "name",
  };
  const trimmedSearch = typeof search === "string" ? search.trim() : "";
  if (trimmedSearch) {
    params.name = trimmedSearch;
  }
  if (Array.isArray(status) && status.length > 0) {
    params.status = status;
  }
  return getListOfPositionsV2({
    account_id,
    queryParamsObj: params,
  });
};

// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_department_create
export const addDepartment = async (account_id, data) => {
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/`,
    data,
  });
  // ER-12439: a new department changes the "not managed" count.
  await refreshAccountCounts(account_id);
  return result;
};

// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_department_update
export const updateDepartment = (account_id, department_id, data) => {
  return axios()({
    method: "put",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/`,
    data,
  });
};

// PATCH method for partial updates to department
export const editDepartment = (account_id, department_id, data) => {
  return axios()({
    method: "patch",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/`,
    data,
  });
};

// DELETE /api/common/v2/account/<ACCOUNT_ID>/department/<DEPT_ID>/
// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_department_delete
export const deleteDepartment = async (account_id, department_id) => {
  let result = await axios()({
    method: "delete",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};

// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_department_activate
export const activateDepartment = async (account_id, department_id) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/activate/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};

// POST /api/common/v2/account/<ACCOUNT_ID>/department/<DEPT_ID>/retire/
// https://api.dev.exiqtive.com/redoc/#tag/common/operation/common_account_department_retire
export const retireDepartment = async (account_id, department_id) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/retire/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};

export const reActivateDepartment = async (account_id, department_id) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/reactivate/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};

export const archiveDepartment = async (account_id, department_id) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/archive/`,
  });
  await refreshAccountCounts(account_id);
  return result;
};

export const unArchiveDepartment = async (account_id, department_id, name) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/unarchive/`,
    ...(name ? { data: { name } } : {}),
  });
  await refreshAccountCounts(account_id);
  return result;
};

// POST /api/common/v2/account/<ACCOUNT_ID>/department/<DEPT_ID>/assign-position/
export const assignPositionToDepartment = async (account_id, department_id, position_id) => {
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/assign-position/`,
    data: { position_id },
  });
  // ER-12439: the position is no longer an orphan.
  await refreshAccountCounts(account_id);
  return result;
};

// POST /api/common/v2/account/<ACCOUNT_ID>/department/<SOURCE_DEPT_ID>/move-position/
export const movePositionToDepartment = async (account_id, department_id, data) => {
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/move-position/`,
    data,
  });
  // ER-12439: moving a position between departments changes the orphan count
  // when the source was the unassigned pool.
  await refreshAccountCounts(account_id);
  return result;
};

// POST /api/common/v2/account/<ACCOUNT_ID>/department/<DEPT_ID>/release-position/
export const releasePositionFromDepartment = async (account_id, department_id, data) => {
  const result = await axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/release-position/`,
    data,
  });
  // ER-12439: releasing to the unassigned pool creates new orphan positions.
  await refreshAccountCounts(account_id);
  return result;
};

// POST /api/common/v2/account/<ACCOUNT_ID>/department/<DEPT_ID>/reorder-positions/
export const reorderPositionsInDepartment = (account_id, department_id, data) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/reorder-positions/`,
    data,
  });
};

// POST /api/common/v2/account/<ACCOUNT_ID>/department/<DEPT_ID>/reorder/
export const reorderDepartment = (account_id, department_id, data) => {
  return axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/department/${department_id}/reorder/`,
    data,
  });
};
